import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import PhotoUploadButton from "@/components/PhotoUploadButton";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useResolvedStorageUrl } from "@/lib/storageObjectUrls";
import {
  ADVENTURE_TYPES,
  extractEventIdFromAdventureDescription,
  stripAdventureMeta,
} from "@/lib/adventure-helpers";
import {
  buildAdventureDescriptionForMode,
  getAdventureSaveTarget,
  type AdventureEditorMode,
} from "@/lib/adventure-editor";
import { supabase } from "@/integrations/supabase/client";
import { normalizeExternalUrl, normalizeWhatsAppChatUrl } from "../../shared/community";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppFormSection,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppText,
  AppTextArea,
} from "../../ui-foundation";

const normaliseAge = (value: string) => {
  if (!value || value === "none") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

type CreateAdventureProps = {
  mode: AdventureEditorMode;
};

const CreateAdventure = ({ mode }: CreateAdventureProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, role } = useAuth();
  const isEditing = mode === "edit";
  const isDuplicating = mode === "duplicate";
  const hasSourceAdventure = mode !== "create";
  const [type, setType] = useState("camping");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [isRange, setIsRange] = useState(false);
  const [location, setLocation] = useState("");
  const [venueUrl, setVenueUrl] = useState("");
  const [whatsappChatUrl, setWhatsappChatUrl] = useState("");
  const [minAge, setMinAge] = useState<string>("");
  const [maxAge, setMaxAge] = useState<string>("");
  const [maxGroupSize, setMaxGroupSize] = useState("6");
  const [linkedEventId, setLinkedEventId] = useState<string | null>(null);
  const [loadingAdventure, setLoadingAdventure] = useState(hasSourceAdventure);
  const [submitting, setSubmitting] = useState(false);
  const resolvedImageUrl = useResolvedStorageUrl("adventure-photos", imageUrl);

  useEffect(() => {
    if (!id || !user) {
      setLoadingAdventure(false);
      return;
    }

    let active = true;

    const loadAdventure = async () => {
      setLoadingAdventure(true);
      const { data, error } = await supabase.from("adventures").select("*").eq("id", id).maybeSingle();

      if (!active) return;

      if (error || !data) {
        toast({ title: "Couldn't load adventure", variant: "destructive" });
        navigate("/activities", { replace: true });
        return;
      }

      if (data.user_id !== user.id && role !== "admin") {
        toast({ title: "You can only edit your own adventures", variant: "destructive" });
        navigate(`/adventures/${id}`, { replace: true });
        return;
      }

      const eventId = extractEventIdFromAdventureDescription(data.description);

      setType(data.type ?? "camping");
      setTitle(data.title ?? "");
      setDescription(stripAdventureMeta(data.description) ?? "");
      setImageUrl(data.image_url ?? null);
      setDateStart(data.date_start ?? "");
      setDateEnd(data.date_end ?? "");
      setIsRange(Boolean(data.date_end));
      setLocation(data.location ?? "");
      setVenueUrl(data.venue_url ?? "");
      setWhatsappChatUrl(data.whatsapp_chat_url ?? "");
      setMinAge(data.min_child_age === null || data.min_child_age === undefined ? "" : String(data.min_child_age));
      setMaxAge(data.max_child_age === null || data.max_child_age === undefined ? "" : String(data.max_child_age));
      setMaxGroupSize(String(data.max_group_size ?? 6));
      setLinkedEventId(isEditing ? eventId : null);
      setLoadingAdventure(false);
    };

    void loadAdventure();

    return () => {
      active = false;
    };
  }, [id, isEditing, navigate, role, user]);

  const submit = async () => {
    if (!user || !title.trim() || !dateStart || !location.trim()) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    const normalizedVenueUrl = venueUrl.trim() ? normalizeExternalUrl(venueUrl) : null;
    if (venueUrl.trim() && !normalizedVenueUrl) {
      toast({
        title: "Add a valid venue website",
        description: "Use a full link beginning with https://",
        variant: "destructive",
      });
      return;
    }
    const normalizedWhatsappChatUrl = whatsappChatUrl.trim()
      ? normalizeWhatsAppChatUrl(whatsappChatUrl)
      : null;
    if (whatsappChatUrl.trim() && !normalizedWhatsappChatUrl) {
      toast({
        title: "Add a valid WhatsApp chat link",
        description: "Paste a WhatsApp invite link beginning with https://chat.whatsapp.com or https://wa.me.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    const payload = {
      type,
      title: title.trim(),
      description: buildAdventureDescriptionForMode(mode, description, linkedEventId),
      image_url: imageUrl,
      location: location.trim(),
      venue_url: normalizedVenueUrl,
      whatsapp_chat_url: normalizedWhatsappChatUrl,
      date_start: dateStart,
      date_end: isRange && dateEnd ? dateEnd : null,
      min_child_age: normaliseAge(minAge),
      max_child_age: normaliseAge(maxAge),
      max_group_size: Number.parseInt(maxGroupSize, 10) || 6,
    };

    const saveTarget = getAdventureSaveTarget(mode, id);
    let errorMessage: string | null = null;
    let createdAdventureId: string | null = null;

    if (saveTarget.kind === "update") {
      let query = supabase.from("adventures").update(payload).eq("id", saveTarget.id);
      if (role !== "admin") {
        query = query.eq("user_id", user.id);
      }
      const result = await query;
      errorMessage = result.error?.message ?? null;
    } else if (isDuplicating) {
      const result = await supabase
        .from("adventures")
        .insert({ ...payload, user_id: user.id })
        .select("id")
        .single();
      errorMessage = result.error?.message ?? null;
      createdAdventureId = result.data?.id ?? null;
    } else {
      const result = await supabase.from("adventures").insert({
        ...payload,
        user_id: user.id,
      });
      errorMessage = result.error?.message ?? null;
    }
    setSubmitting(false);

    if (errorMessage || (isDuplicating && !createdAdventureId)) {
      toast({
        title: "Something went wrong",
        description: errorMessage ?? "The duplicated activity could not be opened.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: isEditing ? "Activity updated" : isDuplicating ? "Activity duplicated" : "Activity posted! 🎉",
    });
    navigate(
      isEditing && id
        ? `/adventures/${id}`
        : isDuplicating && createdAdventureId
          ? `/adventures/${createdAdventureId}`
          : "/activities",
      { replace: true },
    );
  };

  const titleText = isEditing ? "Edit Activity" : isDuplicating ? "Duplicate Activity" : "New Activity";
  const subtitle = isEditing
    ? "Update the activity details families already see."
    : isDuplicating
      ? "Change any details before saving this as a new activity."
      : "Plan a group activity and keep the essentials clear so other families can quickly say yes.";
  const cancelPath = hasSourceAdventure && id ? `/adventures/${id}` : "/activities";

  if (loadingAdventure) {
    return (
      <MemberPageScaffold title={titleText} subtitle={subtitle} fallbackPath="/activities">
        <AppLoadingState title="Loading activity" body="Pulling in the details you saved earlier." />
      </MemberPageScaffold>
    );
  }

  return (
    <MemberPageScaffold title={titleText} subtitle={subtitle} fallbackPath="/activities">
      {linkedEventId ? (
        <AppInlineAlert title="Linked event">
          This activity is connected to a What&apos;s On event, so Kindred will keep that link when you save.
        </AppInlineAlert>
      ) : null}

      <AppCard>
        <AppCardBody gap="$md">
          <AppFormSection
            title="Activity type"
            description="Pick the closest fit so families can scan the list quickly."
          >
            <div className="flex flex-wrap gap-2">
              {ADVENTURE_TYPES.map((adventureType) => (
                <AppButton
                  key={adventureType.value}
                  type="button"
                  size="sm"
                  tone={type === adventureType.value ? "primary" : "outline"}
                  onPress={() => setType(adventureType.value)}
                >
                  {adventureType.emoji} {adventureType.label}
                </AppButton>
              ))}
            </div>
          </AppFormSection>

          <AppFormSection
            title="Cover image"
            description="Optional, but it helps the activity card feel more inviting."
          >
            <div className="overflow-hidden rounded-[24px] border border-border bg-card">
              {resolvedImageUrl ? (
                <img src={resolvedImageUrl ?? ""} alt="" className="h-44 w-full object-cover" />
              ) : (
                <div className="flex h-44 items-end bg-gradient-to-br from-primary/15 via-background to-secondary/10 p-4">
                  <span className="rounded-full bg-card/80 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                    Optional activity cover
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <PhotoUploadButton
                bucket="adventure-photos"
                onPhotoReady={setImageUrl}
                onClear={() => setImageUrl(null)}
                previewUrl={null}
                buttonLabel={imageUrl ? "Change image" : "Upload image"}
                returnObjectPath
              />
              <div className="min-w-0 flex-1">
                <AppText fontSize="$sm" fontWeight="600">
                  {imageUrl ? "Replace cover image" : "Upload cover image"}
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                  This shows on the activity card and detail page.
                </AppText>
              </div>
              {imageUrl ? (
                <AppButton type="button" tone="outline" size="sm" onPress={() => setImageUrl(null)}>
                  Remove
                </AppButton>
              ) : null}
            </div>
          </AppFormSection>

          <AppFormSection title="Details">
            <AppFieldGroup label="Title *">
              <AppInput
                value={title}
                onChangeText={setTitle}
                placeholder="e.g. Summer camping at Delamere"
              />
            </AppFieldGroup>

            <AppFieldGroup
              label="Description"
              helper="What is the plan, what should people bring, and what pace should they expect?"
            >
              <AppTextArea
                value={description}
                onChangeText={setDescription}
                placeholder="What's the plan? What should people know?"
              />
            </AppFieldGroup>
          </AppFormSection>

          <AppFormSection title="When and where">
            <AppFieldGroup label="Date layout" helper="Choose a single date or a date range.">
              <AppButtonRow>
                <AppButton
                  type="button"
                  tone={!isRange ? "secondary" : "outline"}
                  size="sm"
                  onPress={() => setIsRange(false)}
                >
                  Single date
                </AppButton>
                <AppButton
                  type="button"
                  tone={isRange ? "secondary" : "outline"}
                  size="sm"
                  onPress={() => setIsRange(true)}
                >
                  Date range
                </AppButton>
              </AppButtonRow>
            </AppFieldGroup>

            <div className={`grid gap-3 ${isRange ? "sm:grid-cols-2" : ""}`}>
              <AppFieldGroup label={isRange ? "Start date *" : "Date *"}>
                <AppInput
                  type="date"
                  value={dateStart}
                  onChange={(event) => setDateStart(event.target.value)}
                />
              </AppFieldGroup>
              {isRange ? (
                <AppFieldGroup label="End date">
                  <AppInput
                    type="date"
                    value={dateEnd}
                    onChange={(event) => setDateEnd(event.target.value)}
                  />
                </AppFieldGroup>
              ) : null}
            </div>

            <AppFieldGroup label="Location *" helper="Town, city, or a simple flexible note.">
              <AppInput
                value={location}
                onChangeText={setLocation}
                placeholder="Town/city or Flexible"
              />
            </AppFieldGroup>

            <AppFieldGroup
              label="Venue or event website"
              helper="Optional. Add the campsite, venue, booking, or event information page."
            >
              <AppInput
                value={venueUrl}
                onChangeText={setVenueUrl}
                placeholder="https://..."
                inputMode="url"
              />
            </AppFieldGroup>

            <AppFieldGroup
              label="WhatsApp chat link"
              helper="Optional. Paste the WhatsApp invite link for this activity."
            >
              <AppInput
                value={whatsappChatUrl}
                onChangeText={setWhatsappChatUrl}
                placeholder="https://chat.whatsapp.com/..."
                inputMode="url"
              />
            </AppFieldGroup>
          </AppFormSection>

          <AppFormSection title="Family fit">
            <div className="grid gap-3 sm:grid-cols-2">
              <AppFieldGroup label="Min age" helper="Leave blank if there is no minimum.">
                <AppInput
                  type="number"
                  min={0}
                  max={17}
                  value={minAge}
                  onChange={(event) => setMinAge(event.target.value)}
                  placeholder="None"
                />
              </AppFieldGroup>

              <AppFieldGroup label="Max age" helper="Use 18 for an open upper limit.">
                <AppInput
                  type="number"
                  min={0}
                  max={18}
                  value={maxAge}
                  onChange={(event) => setMaxAge(event.target.value)}
                  placeholder="None"
                />
              </AppFieldGroup>
            </div>

            <AppFieldGroup label="Max group size (families)">
              <AppInput
                type="number"
                min={2}
                max={20}
                value={maxGroupSize}
                onChange={(event) => setMaxGroupSize(event.target.value)}
              />
            </AppFieldGroup>
          </AppFormSection>

          <AppButtonRow>
            <AppButton
              type="button"
              tone="outline"
              onPress={() => navigate(cancelPath)}
              flex={1}
            >
              Cancel
            </AppButton>
            {isEditing && id ? (
              <AppButton
                type="button"
                tone="secondary"
                onPress={() => navigate(`/adventures/duplicate/${id}`)}
                flex={1}
              >
                Duplicate activity
              </AppButton>
            ) : null}
            <AppButton type="button" onPress={submit} disabled={submitting} flex={1}>
              {submitting
                ? isEditing
                  ? "Saving..."
                  : isDuplicating
                    ? "Creating..."
                    : "Posting..."
                : isEditing
                  ? "Save changes"
                  : isDuplicating
                    ? "Create duplicate"
                    : "Post activity"}
            </AppButton>
          </AppButtonRow>
        </AppCardBody>
      </AppCard>
    </MemberPageScaffold>
  );
};

export default CreateAdventure;
